import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isNil } from 'lodash';

import { FormGroupControlValueAccessor } from '../../../../../generic/abstract-classes/form-group-control-value-accessor';
import { formToWorkerPermissionsEditorOutput } from '../../editor-output/worker-permissions/form-to-worker-permissions-editor-output/form-to-worker-permissions-editor-output';
import { WorkerPermissionsEditorOutput } from '../../editor-output/worker-permissions/worker-permissions-editor-output';
import { WorkerPermissionsFormType } from '../../editor-output/worker-permissions/worker-permissions-form-type';
import { workerPermissionsToFormType } from '../../editor-output/worker-permissions/worker-permissions-to-form-type/worker-permissions-to-form-type';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => WorkerPermissionsEditorComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => WorkerPermissionsEditorComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'app-worker-permissions-editor',
  templateUrl: './worker-permissions-editor.component.html',
  styleUrls: ['./worker-permissions-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class WorkerPermissionsEditorComponent extends FormGroupControlValueAccessor<
  WorkerPermissionsFormType,
  WorkerPermissionsEditorOutput
> {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  public workerPermissionsOptions = [
    {
      label: 'workerPermissionsUserLabel',
      value: true,
    },
    {
      label: 'workerPermissionsAdminLabel',
      value: false,
    },
  ];

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////
  public defineForm() {
    this.form = new UntypedFormGroup({
      join: new UntypedFormControl(undefined),
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////
  public writeValue(value: WorkerPermissionsEditorOutput) {
    if (isNil(value)) return;

    const formType = workerPermissionsToFormType(value);
    super.writeValue(formType);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////
  public parseValueChange(value: WorkerPermissionsFormType): WorkerPermissionsEditorOutput {
    return formToWorkerPermissionsEditorOutput(value);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////
  protected makeValidationErrors() {
    return {
      'worker-permissions-editor': this.form.value,
    };
  }

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Form Controls
  ///////////////////////////////////////////////////////////////////////////////////////////
  public get join() {
    return this.form.get('join') as UntypedFormControl;
  }

  public get isPermissionSelected() {
    return !isNil(this.join.value);
  }
}
